/**
 * Layout Validation Utilities
 * 
 * Client-side validation for room layout operations
 * Date: 09-10-2025 12:40:09
 * 
 * @module floor-plan-editor/layoutValidation
 */

import type { CanvasBounds, OverlapDetectionResult } from '../../../types/room-layout';

/**
 * Room position interface for overlap detection
 */
interface RoomPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  roomId?: string;
}

/**
 * Check if two room positions overlap
 * Uses AABB (Axis-Aligned Bounding Box) collision detection
 * 
 * @param room1 - First room position
 * @param room2 - Second room position
 * @returns true if rooms overlap, false otherwise
 */
export function checkRoomOverlap(room1: RoomPosition, room2: RoomPosition): boolean {
  return !(
    room1.x + room1.width <= room2.x ||  // room1 is left of room2
    room2.x + room2.width <= room1.x ||  // room2 is left of room1
    room1.y + room1.height <= room2.y || // room1 is above room2
    room2.y + room2.height <= room1.y    // room2 is above room1
  );
}

/**
 * Detect overlaps between a room and all other rooms
 * 
 * @param room - Room to check
 * @param allRooms - All other rooms on the floor
 * @param excludeRoomId - Optional room ID to exclude from check
 * @returns Overlap detection result
 */
export function detectOverlaps(
  room: RoomPosition,
  allRooms: RoomPosition[],
  excludeRoomId?: string
): OverlapDetectionResult {
  const overlappingRoomIds: string[] = [];

  for (const otherRoom of allRooms) {
    // Skip if this is the same room
    if (otherRoom.roomId === excludeRoomId) {
      continue;
    }

    if (checkRoomOverlap(room, otherRoom)) {
      if (otherRoom.roomId) {
        overlappingRoomIds.push(otherRoom.roomId);
      }
    }
  }

  return {
    hasOverlap: overlappingRoomIds.length > 0,
    overlappingRoomIds
  };
}

/**
 * Check if room is within canvas bounds
 * 
 * @param room - Room position to check
 * @param bounds - Canvas bounds
 * @returns true if room is within bounds
 */
export function isWithinBounds(room: RoomPosition, bounds: CanvasBounds): boolean {
  return (
    room.x >= bounds.minX &&
    room.y >= bounds.minY &&
    room.x + room.width <= bounds.maxX &&
    room.y + room.height <= bounds.maxY
  );
}

/**
 * Snap value to grid
 * 
 * @param value - Value to snap
 * @param gridSize - Grid size (default: 20)
 * @returns Snapped value
 */
export function snapToGrid(value: number, gridSize: number = 20): number {
  return Math.round(value / gridSize) * gridSize;
}

/**
 * Snap room position to grid
 * 
 * @param room - Room position to snap
 * @param gridSize - Grid size (default: 20)
 * @returns Snapped room position
 */
export function snapRoomToGrid(room: RoomPosition, gridSize: number = 20): RoomPosition {
  return {
    ...room,
    x: snapToGrid(room.x, gridSize),
    y: snapToGrid(room.y, gridSize),
    width: snapToGrid(room.width, gridSize),
    height: snapToGrid(room.height, gridSize)
  };
}

/**
 * Constrain room position to canvas bounds
 * 
 * @param room - Room position to constrain
 * @param bounds - Canvas bounds
 * @returns Constrained room position
 */
export function constrainToBounds(room: RoomPosition, bounds: CanvasBounds): RoomPosition {
  const x = Math.max(bounds.minX, Math.min(room.x, bounds.maxX - room.width));
  const y = Math.max(bounds.minY, Math.min(room.y, bounds.maxY - room.height));

  return {
    ...room,
    x,
    y
  };
}

/**
 * Calculate canvas bounds based on stage dimensions
 * 
 * @param stageWidth - Stage width
 * @param stageHeight - Stage height
 * @param padding - Padding from edges (default: 20)
 * @returns Canvas bounds
 */
export function calculateCanvasBounds(
  stageWidth: number,
  stageHeight: number,
  padding: number = 20
): CanvasBounds {
  return {
    minX: padding,
    minY: padding,
    maxX: stageWidth - padding,
    maxY: stageHeight - padding
  };
}

/**
 * Validate room dimensions
 * 
 * @param width - Room width
 * @param height - Room height
 * @param minSize - Minimum size (default: 50)
 * @param maxSize - Maximum size (default: 500)
 * @returns Validation result with error message
 */
export function validateRoomDimensions(
  width: number,
  height: number,
  minSize: number = 50,
  maxSize: number = 500
): { isValid: boolean; error?: string } {
  if (width < minSize || height < minSize) {
    return {
      isValid: false,
      error: `Room dimensions must be at least ${minSize}x${minSize} pixels`
    };
  }

  if (width > maxSize || height > maxSize) {
    return {
      isValid: false,
      error: `Room dimensions cannot exceed ${maxSize}x${maxSize} pixels`
    };
  }

  return { isValid: true };
}


